import './componentsPlugin.less';
import React, { useCallback } from 'react';
import tinymce from 'tinymce';
import { Editor } from 'tinymce';

import { toJS } from 'mobx';
import _ from 'lodash';

import { AntModal, createContainer, ModalPosition } from 'controls/react/ant/antModal';

import { components, getComponentDescription } from 'vendor-init/tinymce/plugins/components';

import { sharedLocalization } from 'vendor-init/tinymce/plugins/localization';
import { ComponentDescription } from 'vendor-init/tinymce/plugins/componentDescription';
import { observer } from 'mobx-react';
import { ValidatableModel } from 'framework/mobx-integration';
import {
	AttributesConfig,
	ComponentAttribute,
	ComponentAttributes,
} from 'vendor-init/tinymce/plugins/components/attributes';
import { Section } from 'controls/react/layout/section';
import { once } from 'lodash/fp';

const i = require('core/localization/localization').translator(sharedLocalization);

const isNotDataAttribute = (name: string) => {
	return ['title', 'id', 'type'].find((x) => x == name) != null;
};

function getObjectAttributeName(imgAttributeName: string) {
	const pureName = imgAttributeName.startsWith('data') ? imgAttributeName.substring(5) : imgAttributeName;
	if (isNotDataAttribute(pureName)) return pureName;

	return imgAttributeName;
}

function getImgAttributeName(objectAttributeName: string) {
	if (isNotDataAttribute(objectAttributeName)) return 'data-' + objectAttributeName;

	return objectAttributeName;
}

function getPreviewUrl(imageUrl: string) {
	return imageUrl;
}

function isReservedField(x: string) {
	return ['validator', 'attributes', 'destroy', 'mobx'].includes(x);
}

function updateElementFromConfig(element: HTMLImageElement, config: Record<string, any>) {
	const configPlain = toJS(config);
	Object.keys(configPlain).forEach((x) => {
		let value = null;
		// we set value to null to remove attribute if it's reserved.
		// This will fix corrupted data created before.
		if (!isReservedField(x)) {
			value = configPlain[x];
		}

		if (Array.isArray(value)) {
			value = JSON.stringify(value);
		}

		const attributeName = _.kebabCase('data-' + x);
		if (value === '' || value === null || value === undefined) {
			element.removeAttribute(attributeName);
		} else {
			element.setAttribute(attributeName, value);
		}
	});

	config.attributes.allAttributes?.forEach((attr: ComponentAttribute) => {
		const attributeName = 'data-' + attr.originalKey;
		element.removeAttribute(attributeName);
	});

	config.attributes.attributes?.forEach((attr: ComponentAttribute) => {
		const attributeName = 'data-' + attr.key;
		element.setAttribute(attributeName, attr.value);
	});
}

function getAttributeValue(value: string) {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

function getConfigFromElement<T extends AttributesConfig & Record<string, any>, C extends new () => T>(
	element: HTMLImageElement,
	configClass: C,
) {
	const config = new configClass();
	const allClassKeys = Object.getOwnPropertyNames(config);
	config.attributes.reservedKeys = allClassKeys;

	element.getAttributeNames().forEach((x) => {
		if (!x.startsWith('data')) return;

		const key = x.replace('data-', '');
		const propertyName = _.camelCase(key);

		if (key.startsWith('mce') || key == 'type') {
			return;
		}

		if (isReservedField(propertyName)) return;

		const rawValue = element.getAttribute(x);

		if (!allClassKeys.includes(propertyName)) {
			config.attributes.add(key, rawValue);
		} else {
			const value = getAttributeValue(rawValue);
			(config as Record<string, any>)[propertyName] = value;
		}
	});

	return config;
}

function editSelectedComponent(editor: Editor) {
	const element = editor.selection.getNode() as HTMLImageElement;

	const componentDescription = getComponentDescription(element);
	if (componentDescription == null) return;

	const config = getConfigFromElement(element, componentDescription.config);

	// @ts-ignore
	return openForm(componentDescription, config, 'update', () => {
		//editor.selection.dom.getAttrib()

		updateElementFromConfig(element, config);

		editor.nodeChanged();
	});
}

tinymce.PluginManager.add('ceeview-components', function (editor, url) {
	const modalsDestroyers: { (): void }[] = [];

	editor.on('init', () => {
		const container = editor.getWin().document.body;
		container.addEventListener('dblclick', (e) => {
			const element = e.target as Element;

			if (element.tagName == 'IMG' && element.getAttribute('data-type')) {
				const destroyer = editSelectedComponent(editor);
				modalsDestroyers.push(destroyer);
			}
		});
	});

	for (const componentDescription of components) {
		//const componentDescription = a as ComponentDescription<object>
		editor.ui.registry.addMenuItem('component-' + componentDescription.id, {
			text: componentDescription.title,
			onAction: function () {
				const config = new componentDescription.config({ newConfig: true });

				// @ts-ignore
				const destroyer = openForm(componentDescription, config, 'create', () => {
					const element = document.createElement('img');
					element.style.width = componentDescription.defaultSize.width;
					element.style.height = componentDescription.defaultSize.height;
					element.setAttribute('data-type', componentDescription.type ?? componentDescription.id);
					element.src = getPreviewUrl(componentDescription.preview);
					element.title = componentDescription.title;

					updateElementFromConfig(element, config);

					editor.insertContent(element.outerHTML);
				});

				modalsDestroyers.push(destroyer);
			},
		});
	}

	editor.ui.registry.addMenuItem('component-edit', {
		text: 'Edit component',
		onAction: function () {
			editSelectedComponent(editor);
		},
	});

	editor.ui.registry.addContextMenu('ceeview-components', {
		update: function (element) {
			if (!element.getAttribute('data-type') || !getComponentDescription(element)) {
				return '';
			}

			return 'component-edit';
		},
	});

	editor.on('GetContent', function (e) {
		if (!e.content) return;

		const container = document.createElement('div');
		container.innerHTML = e.content;

		container.querySelectorAll('img[data-type]').forEach((img) => {
			const obj = document.createElement('object');
			img.getAttributeNames().forEach((x) => {
				if (x == 'src' || x.startsWith('data-mce') || x == 'title') return;

				obj.setAttribute(getObjectAttributeName(x), img.getAttribute(x));
			});

			img.parentNode.appendChild(obj);
			img.parentNode.removeChild(img);
		});

		e.content = container.innerHTML;

		e.content = e.content.replaceAll(/<(img|hr|br)(.*?)>/gm, '<$1$2/>');
	});

	editor.on('BeforeSetContent', function (e) {
		if (!e) return;

		const container = document.createElement('div');
		container.innerHTML = e.content;

		container.querySelectorAll('object[type]').forEach((obj) => {
			const img = document.createElement('img');
			obj.getAttributeNames().forEach((x) => {
				img.setAttribute(getImgAttributeName(x), obj.getAttribute(x));
			});

			const description = getComponentDescription(img);

			if (description == null) {
				return;
			}

			img.src = getPreviewUrl(description.preview);

			obj.parentNode.appendChild(img);
			obj.parentNode.removeChild(obj);
		});

		e.content = container.innerHTML;
	});

	editor.on('remove', () => {
		modalsDestroyers.forEach((x) => x());
		modalsDestroyers.length = 0;
	});
});

const b = require('b_').with('builder-form');
function openForm<T extends ValidatableModel<T> & AttributesConfig & { destroy?: () => void }>(
	description: ComponentDescription<T>,
	config: T,
	mode: 'create' | 'update',
	onOk: () => void,
) {
	const [root, destroy, container] = createContainer(document.body);
	container.className = b();
	const destroyWindow = once(() => {
		destroy();
		config.destroy?.();
	});

	root.render(
		<ConfigEditor
			description={description}
			mode={mode}
			config={config}
			onCancel={destroyWindow}
			onOk={() => {
				(destroyWindow(), onOk());
			}}
		/>,
	);

	return destroyWindow;
}

type ConfigEditorProps<T extends ValidatableModel<T> & AttributesConfig> = {
	description: ComponentDescription<T>;
	config: T;
	onCancel: () => void;
	onOk: () => void;
	mode: 'create' | 'update';
};

const ConfigEditor = observer(<T extends ValidatableModel<T> & AttributesConfig>(props: ConfigEditorProps<T>) => {
	const Form = props.description.form;

	const validationErrors = props.config.validator.errors.map((x) => x.errors.join('\r\n')).join('\r\n');

	return (
		<AntModal
			mode={props.mode}
			title={props.description.title}
			visible={true}
			positionType={ModalPosition.Centered}
			onOk={props.onOk}
			okButtonProps={{ disabled: !props.config.validator.valid, title: validationErrors }}
			onCancel={props.onCancel}
		>
			<Section appearance={'none'} direction={'column'} contentClass={b()} contentPadding={true}>
				<Form config={props.config} />
				<ComponentAttributes config={props.config} className={b('attributes-editor')} />
			</Section>
		</AntModal>
	);
});
